import {
  ContractType,
  InsuranceProviderQuoteResponse,
  PolicyCoverResponse,
  PolicyDetailsResponse,
  PolicyPayload,
  PolicyState,
  PolicyStatus,
  QuoteInsurerInfoProps,
  calculateTimeBetweenDates,
  canCancelPolicy,
  canCancelRenewalPolicy,
  formatCommonDateStr,
  getQuoteCurrentInsuranceInfo,
  judgeWhetherIsLaterThanTheDate,
  policyIsRenewPolicy,
} from '@jove-frontend/uikit';
import dayjs, { Dayjs } from 'dayjs';
import { cloneDeep } from 'lodash';
import BaseModel from '../BaseModel';

class PolicyModel extends BaseModel implements PolicyDetailsResponse, QuoteInsurerInfoProps {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  quoteId?: string;
  payload?: PolicyPayload;
  revision?: number;
  providerCode?: string;
  providerReference?: string;
  userId?: string;
  contractType?: ContractType;
  expirationDate?: string;
  expirationDateText?: string;
  expirationDateDayjs?: Dayjs;
  stripeSubscriptionId?: string;
  status: PolicyStatus;
  state: PolicyState;
  pauseStartDate?: string;
  pauseEndDate?: string;
  cancellationDate?: string;
  activeDate?: string;
  notified: boolean;
  viewedAt: string;
  insuranceProviderQuote?: InsuranceProviderQuoteResponse;
  covers?: PolicyCoverResponse[];
  overview?: any;
  expiringSoon?: boolean;
  expirationReminder?: string;
  pauses?: any[];
  policyNumber?: string;
  // Insurance
  isMsAmlin?: boolean;
  isChubb?: boolean;
  isAccelerant?: boolean;
  isGit?: boolean;
  isBroker?: boolean;
  isSagic?: boolean;
  isGreateAmerican?: boolean;
  isGreateAmericanBussinessInsurance?: boolean;
  isUnknownInsurance?: boolean;
  isRecruitment?: boolean;

  policyStartDate?: string;
  policyStartDateText?: string;
  policyStartDateDayjs?: Dayjs;
  policyEndDate?: string;
  policyEndDateText?: string;
  policyEndDateDayjs?: Dayjs;
  pauseStartDateText?: string;
  pauseStartDateDayjs?: Dayjs;
  pauseEndDateDayjs?: Dayjs;
  // can show cover list view
  canShowCovers: boolean;
  // can show pausing summary view
  canShowPausingSummary: boolean;
  isActive: boolean;
  isPending: boolean;
  isPaused: boolean;
  isExpiredSoon: boolean;
  isExpired: boolean;
  isCanceled: boolean;
  isWeeksExtended: boolean;
  isToBePaused: boolean;
  isToBeCancel: boolean;
  isErnPending: boolean;
  isElapsed: boolean;
  // can pause policy
  canPause: boolean;
  // can update
  canUpdate: boolean;
  // can cancel
  canCancel: boolean;
  // can cancel
  canCancelRenewal: boolean;
  // can make a claim
  canMakeClaim: boolean;
  // can unpause
  canUnpause: boolean;
  // can extend pause
  canExtendPause: boolean;
  // can renew pause
  canRenew: boolean;
  // can switch to annual
  canSwitchToAnnual: boolean;
  // can extend policy
  canExtendPolicy: boolean;
  // can get new quote
  canGetNewQuote: boolean;
  // can cancel pause
  canCancelPause: boolean;
  // can edit pause
  canEditPause: boolean;
  // remaining days
  dayLeft: number;
  // expired date text
  expiredDateText: string;
  // canceled date text
  canceledDateText: string;
  // The minimum handle time allowed
  allowHandleStartDateDayjs: Dayjs;
  // Renewal
  isRenewing: boolean;
  // Renewal policy
  isRenewalPolicy: boolean;

  // Quote data
  quotePayload?: any;
  coverOptions?: any;
  renewalQuotePayload?: any;
  renewalCoversOptions?: any;
  prices?: any;
  renewalPrices?: any;
  haveDraft?: boolean;
  quoteDraft: {
    covers_options?: any;
    prices?: any;
    quote_payload?: any;
  };
  renewalDraftQuotePayload?: any;
  renewalDraftPrices?: any;
  renewalCancelled?: boolean;

  transformServeDataToInstance(data) {
    super.transformServeDataToInstance(data);

    // Special handler
    this.coverOptions = data.coversOptions;

    const insuranceInfo = getQuoteCurrentInsuranceInfo({
      providerCode: data?.providerCode,
    });
    Object.keys(insuranceInfo).forEach((key) => {
      this[key] = insuranceInfo[key];
    });

    // flag
    this.isExpiredSoon = this.expirationReminder === 'expiring_soon';
    this.isWeeksExtended = this.expirationReminder === 'two_weeks_extended';
    this.isToBePaused = [PolicyState.POLICY_ACTIVE].includes(this.state) && [PolicyStatus.POLICY_PAUSE_PLANNED].includes(this.status);
    this.isToBeCancel =
      ![PolicyState.POLICY_CANCELLED].includes(this.state) &&
      [PolicyStatus.POLICY_CANCEL_INITIATED, PolicyStatus.POLICY_CANCEL_CONFIRMED].includes(this.status);
    this.isActive = [PolicyState.POLICY_ACTIVE].includes(this.state) && !this.isExpiredSoon && !this.isToBePaused && !this.isToBeCancel;
    this.isPending = [PolicyState.POLICY_PENDING].includes(this.state) && !this.isExpiredSoon;
    this.isPaused = [PolicyState.POLICY_PAUSED].includes(this.state) && !this.isExpiredSoon;
    this.isCanceled = [PolicyState.POLICY_CANCELLED].includes(this.state) && !this.isExpiredSoon;
    this.isRenewalPolicy = policyIsRenewPolicy(this);
    // this.isExpired =
    //   [PolicyState.POLICY_ELAPSED].includes(this.state) && !this.isExpiredSoon;
    this.isElapsed = [PolicyState.POLICY_ELAPSED].includes(this.state);

    this.handleTheLogicRelatedToOperation();

    this.isErnPending = ['ern_pending'].includes(this.status);
    this.policyStartDate = this.payload?.policy_start_date;
    this.policyEndDate = this.payload?.policy_end_date;
    // time
    if (judgeWhetherIsLaterThanTheDate(this.pauseStartDate, new Date())) {
      this.dayLeft = calculateTimeBetweenDates(this.expirationDate, this.pauseStartDate);
    } else {
      this.dayLeft = calculateTimeBetweenDates(this.expirationDate, new Date());
    }
    this.expiredDateText = formatCommonDateStr(this.expirationDate);
    this.canceledDateText = formatCommonDateStr(this.cancellationDate);
    if (this.pauseStartDate) {
      this.pauseStartDateDayjs = dayjs(this.pauseStartDate);
      this.pauseStartDateText = formatCommonDateStr(this.pauseStartDate);
    }
    if (this.pauseEndDate) this.pauseEndDateDayjs = dayjs(this.pauseEndDate);
    if (this.policyStartDate) {
      this.policyStartDateDayjs = dayjs(this.policyStartDate);
      this.policyStartDateText = formatCommonDateStr(this.policyStartDate);
      // Compared with today
      if (judgeWhetherIsLaterThanTheDate(dayjs(new Date()).endOf('day'), this.policyStartDateDayjs)) {
        this.allowHandleStartDateDayjs = dayjs(new Date());
      } else {
        this.allowHandleStartDateDayjs = cloneDeep(this.policyStartDateDayjs);
      }
    } else {
      this.allowHandleStartDateDayjs = dayjs(new Date());
    }
    if (this.policyEndDate) {
      this.policyEndDateDayjs = dayjs(this.policyEndDate);
      this.policyEndDateText = formatCommonDateStr(this.policyEndDate);
    }
    if (this.expirationDate) {
      this.expirationDateDayjs = dayjs(this.expirationDate);
      this.expirationDateText = formatCommonDateStr(this.expirationDate);
    }
  }

  handleTheLogicRelatedToOperation() {
    // judgement
    this.canShowCovers = ![PolicyState.POLICY_ELAPSED, PolicyState.POLICY_CANCELLED].includes(this.state);
    this.canMakeClaim = this.isChubb && ![PolicyState.POLICY_PAUSED, PolicyState.POLICY_ELAPSED, PolicyState.POLICY_CANCELLED].includes(this.state);
    this.canGetNewQuote = [PolicyState.POLICY_ELAPSED, PolicyState.POLICY_CANCELLED].includes(this.state);
    this.canUpdate =
      !!this.canUpdate ||
      [
        PolicyStatus.POLICY_UPDATE_INITIATED,
        PolicyStatus.POLICY_UPDATE_QUOTE,
        PolicyStatus.POLICY_RENEWED_INITIATED,
        PolicyStatus.POLICY_RENEWED_UPDATE_QUOTE,
        PolicyStatus.POLICY_UPDATE_REFERRAL,
        PolicyStatus.POLICY_RENEWED_UPDATE_REFERRAL,
      ].includes(this.status);

    if (this.isChubb) {
      switch (this.contractType) {
        case ContractType.ANNUAL:
          this.canShowPausingSummary = [PolicyState.POLICY_PAUSED].includes(this.state);
          this.canPause =
            [PolicyState.POLICY_ACTIVE, PolicyState.POLICY_PENDING].includes(this.state) &&
            !this.isExpiredSoon &&
            !this.isToBePaused &&
            !this.isPending &&
            !this.isToBeCancel;
          this.canCancel = [PolicyState.POLICY_ACTIVE, PolicyState.POLICY_PENDING, PolicyState.POLICY_PAUSED].includes(this.state);
          this.canUnpause = [PolicyState.POLICY_PAUSED].includes(this.state);
          this.canExtendPause = [PolicyState.POLICY_PAUSED].includes(this.state);
          this.canRenew = this.expirationReminder === 'expiring_soon';
          this.canCancelPause = this.isToBePaused;
          this.canEditPause = this.isToBePaused;
          break;
        case ContractType.MONTHLY:
          this.canSwitchToAnnual = ![PolicyState.POLICY_ELAPSED, PolicyState.POLICY_CANCELLED].includes(this.state);
          this.canExtendPolicy = ![PolicyState.POLICY_ELAPSED, PolicyState.POLICY_CANCELLED].includes(this.state);
          this.canCancel = [PolicyState.POLICY_ACTIVE].includes(this.state);
          break;
        default:
          break;
      }
    } else if (this.isGreateAmerican) {
      this.canShowPausingSummary = false;
      this.canPause = false;
      this.canCancel = canCancelPolicy(this);
      this.canUnpause = false;
      this.canExtendPause = false;
      this.canRenew = !!this.renewalQuotePayload || [PolicyStatus.POLICY_RENEWED_INITIATED].includes(this.status);
      this.canCancelPause = false;
      this.canEditPause = false;
      this.canSwitchToAnnual = false;
      this.canExtendPolicy = false;
      this.canUpdate = false;
    } else if (this.isAccelerant || this.isRecruitment || this.isGit) {
      this.canShowPausingSummary = false;
      this.canPause = false;
      this.canCancel = canCancelPolicy(this);
      this.canCancelRenewal = canCancelRenewalPolicy(this);
      this.canUnpause = false;
      this.canExtendPause = false;
      this.canRenew = !!this.renewalQuotePayload || [PolicyStatus.POLICY_RENEWED_INITIATED].includes(this.status);
      this.canCancelPause = false;
      this.canEditPause = false;
      this.canSwitchToAnnual = false;
      this.canExtendPolicy = false;
    } else if (this.isGreateAmericanBussinessInsurance) {
      this.canShowPausingSummary = false;
      this.canPause = false;
      this.canCancel = canCancelPolicy(this);
      this.canUnpause = false;
      this.canExtendPause = false;
      this.canRenew = false;
      this.canCancelPause = false;
      this.canEditPause = false;
      this.canSwitchToAnnual = false;
      this.canExtendPolicy = false;
      this.canUpdate = false;
    } else {
      this.canShowPausingSummary = false;
      this.canPause = false;
      this.canCancel = false;
      this.canUnpause = false;
      this.canExtendPause = false;
      this.canRenew = false;
      this.canCancelPause = false;
      this.canEditPause = false;
      this.canSwitchToAnnual = false;
      this.canExtendPolicy = false;
    }
  }
}

export default PolicyModel;
